import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';

// Bridges an OS-delivered deep link into an in-app sign-in, so the emailed magic
// link completes *inside* the native app instead of dead-ending in a browser.
//
// The link arrives one of two ways depending on the shell:
//   - tablet (Capacitor):  https://<host>/sign-in/TOKEN  via Android App Links, or
//                          tableview://sign-in/TOKEN      via the custom-scheme filter
//   - desktop (Electron):  tableview://sign-in/TOKEN      via a custom protocol,
//                          forwarded from the main process as window.electronAPI.onDeepLink
//
// Either way we pull the token and route to /sign-in/:token, where SignInLink
// peeks -> challenges for 2FA if owed -> consumes and sets the device cookie.
// HashRouter-safe: navigate() drives the router directly, so whether the token
// sits in the URL path or the hash doesn't matter.
export default function DeepLinkHandler(): null {
  const navigate = useNavigate();

  useEffect(() => {
    const routeFromUrl = (url?: string) => {
      if (!url) return;
      // /open-app/ as well as /sign-in/: the emailed desktop button points at the
      // hand-off page, and on Android an App Link or the tableview:// filter can deliver
      // that URL here instead of a browser. Same token either way.
      const m = url.match(/\/(?:sign-in|open-app)\/([^/?#]+)/);
      if (m) navigate(`/sign-in/${m[1]}`, { replace: true });
    };

    // Tablet / Android App Links. No-op on web and on the Electron desktop build.
    let handle: { remove: () => void } | undefined;
    CapacitorApp.addListener('appUrlOpen', (e) => routeFromUrl(e.url))
      .then((h) => { handle = h; })
      .catch(() => { /* not running on a Capacitor host */ });

    // Desktop / Electron custom protocol. The bridge (window.electronAPI.onDeepLink)
    // is added in electron-app main + preload; absent until that ships, hence the guard.
    const electronAPI = (window as any).electronAPI;
    if (electronAPI && typeof electronAPI.onDeepLink === 'function') {
      electronAPI.onDeepLink(routeFromUrl);
    }

    return () => { if (handle) handle.remove(); };
  }, [navigate]);

  return null;
}
